import { useState } from 'react';
import { DemandaTable, Pagination, Badge, AlertModal, ConfirmModal } from '../../components';
import type { Demanda } from '../../models';

// Dados de exemplo para demonstrar a tabela
const generateMockDemandas = (count: number): Demanda[] => {
  const mockData: Demanda[] = [];
  const statuses = ['EMATRIBUICAO', 'ATRIBUIDA', 'EMPROGRESSO', 'AGUARDANDOAUTORIZACAO', 'SOLUCIONADA', 'FINALIZADA', 'DEVOLVIDA', 'ERRADA', 'RECUSADA', 'EXCLUIDA'] as const;
  const priorities = ['Baixa', 'Media', 'Alta', 'Urgente'] as const;
  const setores = ['Tecnologia da Informação', 'Recursos Humanos', 'Financeiro', 'Marketing', 'Operações'];
  const usuarios = [
    { id: 1, name: 'João Silva', email: 'joao.silva@empresa.com', funcao_usuario: 'ANALISTA', cargo_usuario: 'Analista de Sistemas' },
    { id: 2, name: 'Maria Santos', email: 'maria.santos@empresa.com', funcao_usuario: 'COORDENADOR', cargo_usuario: 'Coordenadora de RH' },
    { id: 3, name: 'Carlos Oliveira', email: 'carlos.oliveira@empresa.com', funcao_usuario: 'FUNCIONARIO', cargo_usuario: 'Desenvolvedor' },
    { id: 4, name: 'Ana Costa', email: 'ana.costa@empresa.com', funcao_usuario: 'GESTOR', cargo_usuario: 'Gerente Financeiro' },
    { id: 5, name: 'Pedro Lima', email: 'pedro.lima@empresa.com', funcao_usuario: 'FUNCIONARIO', cargo_usuario: 'Especialista em Marketing' },
    { id: 6, name: 'Fernanda Souza', email: 'fernanda.souza@empresa.com', funcao_usuario: 'COORDENADOR', cargo_usuario: 'Coordenadora de Operações' },
  ];

  for (let i = 1; i <= count; i++) {
    const randomUser1 = usuarios[Math.floor(Math.random() * usuarios.length)];
    const randomUser2 = usuarios[Math.floor(Math.random() * usuarios.length)];
    const randomSetor = setores[Math.floor(Math.random() * setores.length)];
    const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
    const randomPriority = priorities[Math.floor(Math.random() * priorities.length)];
    
    // Gerar data aleatória nos próximos 30 dias
    const randomDate = new Date();
    randomDate.setDate(randomDate.getDate() + Math.floor(Math.random() * 30));

    const demandaData: Demanda = {
      id: i,
      titulo: `Demanda ${i.toString().padStart(3, '0')} - ${getRandomTitle()}`,
      descricao: getRandomDescription(),
      prioridade: randomPriority,
      status: randomStatus,
      previsao_entrega: randomDate.toISOString().split('T')[0],
      sugestao_entrega: randomDate.toISOString().split('T')[0],
      demandante: randomUser1,
      setor: {
        id: Math.floor(Math.random() * 5) + 1,
        nome_setor: randomSetor,
        cidade_fk: 1, // Valor padrão
        equipe: false,
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    };

    // Adicionar colaborador atribuído apenas se sorteado (70% chance)
    if (Math.random() > 0.3) {
      demandaData.demandado = randomUser2;
    }

    mockData.push(demandaData);
  }

  return mockData;
};

const getRandomTitle = () => {
  const titles = [
    'Implementar sistema de autenticação',
    'Criar relatórios gerenciais',
    'Otimizar performance do sistema',
    'Desenvolver nova funcionalidade',
    'Corrigir bugs identificados',
    'Integração com API externa',
    'Melhorar interface do usuário',
    'Configurar ambiente de produção',
    'Atualizar documentação técnica',
    'Realizar testes de segurança',
  ];
  return titles[Math.floor(Math.random() * titles.length)];
};

const getRandomDescription = () => {
  const descriptions = [
    'Esta demanda envolve o desenvolvimento de uma solução completa para atender aos requisitos especificados.',
    'Necessário realizar análise detalhada e implementar as melhorias identificadas no processo.',
    'Projeto estratégico que impactará diretamente na experiência do usuário final.',
    'Tarefa crítica para manutenção da estabilidade e performance do sistema.',
    'Desenvolvimento de funcionalidade essencial para o crescimento do negócio.',
  ];
  return descriptions[Math.floor(Math.random() * descriptions.length)];
};

export const TablesPage = () => {
  // Estados para controle da paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  // Estados para modais
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'info' | 'warning' | 'error' | 'success';
    message: string;
    title?: string;
  }>({ isOpen: false, type: 'info', message: '' });
  
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'info' | 'warning' | 'danger' | 'success';
    message: string;
    details?: string;
    title?: string;
    onConfirm?: () => void;
  }>({ isOpen: false, type: 'info', message: '' });

  // Funções auxiliares para modais
  const showAlert = (type: 'info' | 'warning' | 'error' | 'success', message: string, title?: string) => {
    setAlertModal({ isOpen: true, type, message, ...(title ? { title } : {}) });
  };

  const showConfirm = (
    type: 'info' | 'warning' | 'danger' | 'success', 
    message: string, 
    onConfirm: () => void,
    details?: string,
    title?: string
  ) => {
    setConfirmModal({ 
      isOpen: true, 
      type, 
      message, 
      onConfirm,
      ...(details ? { details } : {}),
      ...(title ? { title } : {}) 
    });
  };

  // Dados para demonstração de badges
  const badgeVariants = ['success', 'warning', 'error', 'danger', 'info', 'default'] as const;
  const badgeStyles = ['solid', 'outline', 'ghost'] as const;
  const badgeSizes = ['sm', 'md', 'lg'] as const;

  // Gerar dados mockados
  const totalItems = 25; // Número menor para demonstração
  const allDemandas = generateMockDemandas(totalItems);
  
  // Calcular paginação
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDemandas = allDemandas.slice(startIndex, endIndex);

  // Handlers para ações da tabela
  const handleView = (demanda: Demanda) => {
    showAlert('info', `Visualizar demanda: ${demanda.titulo}`, 'Visualizar');
  };

  const handleEdit = (demanda: Demanda) => {
    showAlert('info', `Editar demanda: ${demanda.titulo}`, 'Editar');
  };

  const handleDelete = (demanda: Demanda) => {
    showConfirm(
      'danger',
      `Tem certeza que deseja excluir a demanda "${demanda.titulo}"?`,
      () => {
        showAlert('success', `Demanda "${demanda.titulo}" foi excluída!`, 'Sucesso');
      },
      'Esta ação não pode ser desfeita.',
      'Excluir Demanda'
    );
  };

  // Simular loading
  const handleLoadingTest = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  // Handler para mudança de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Simular loading ao mudar página
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 300);
  };

  // Handler para mudança de itens por página
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset para primeira página
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Tables & Badges
        </h1>
        <p className="text-gray-600">
          Componentes de tabela genérica, paginação e badges para o sistema de demandas
        </p>
      </div>

      {/* Seção de Badges */}
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-900">Componente Badge</h2>
        
        {/* Badges por Estilo */}
        {badgeStyles.map((style) => (
          <div key={style} className="bg-white rounded-lg shadow overflow-hidden p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 capitalize">
              Estilo: {style}
            </h3>
            
            <div className="space-y-4">
              {badgeVariants.map((variant) => (
                <div key={variant} className="flex items-center space-x-4">
                  <div className="w-20 text-sm font-medium text-gray-700 capitalize">
                    {variant}:
                  </div>
                  <div className="flex items-center space-x-3">
                    {badgeSizes.map((size) => (
                      <Badge 
                        key={size}
                        variant={variant}
                        style={style}
                        size={size}
                      >
                        {size.toUpperCase()}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Exemplos de Uso Prático dos Badges */}
        <div className="bg-white rounded-lg shadow overflow-hidden p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Exemplos de Uso - Status de Demandas
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="font-medium text-gray-800">Status</h4>
              <div className="flex flex-wrap gap-2">
                <Badge variant="warning" size="sm">Em Atribuição</Badge>
                <Badge variant="info" size="sm">Em Atendimento</Badge>
                <Badge variant="success" size="sm">Finalizada</Badge>
                <Badge variant="error" size="sm">Cancelada</Badge>
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium text-gray-800">Prioridades</h4>
              <div className="flex flex-wrap gap-2">
                <Badge variant="success" style="outline" size="sm">Baixa</Badge>
                <Badge variant="warning" style="outline" size="sm">Média</Badge>
                <Badge variant="error" style="outline" size="sm">Alta</Badge>
                <Badge variant="error" size="sm">Urgente</Badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Seção de Tabela */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900">Componente Table</h2>
          <div className="flex gap-2">
            <button
              onClick={handleLoadingTest}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
              disabled={isLoading}
            >
              {isLoading ? 'Carregando...' : 'Testar Loading'}
            </button>
            <button
              onClick={() => setCurrentPage(1)}
              className="px-3 py-1 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
            >
              Página 1
            </button>
          </div>
        </div>

        {/* Tabela Principal */}
        <div className="shadow">
          <DemandaTable
            demandas={currentDemandas}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            isLoading={isLoading}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            showItemsPerPage={true}
            itemsPerPageOptions={[5, 10, 15, 25]}
          />
        </div>

        {/* Estados da Tabela */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Estado de Loading */}
          <div className="bg-white rounded-lg shadow overflow-hidden p-4">
            <h4 className="font-medium text-gray-800 mb-3">Estado de Loading</h4>
            <div className="bg-white rounded-md overflow-hidden">
              <DemandaTable
                demandas={[]}
                isLoading={true}
              />
            </div>
          </div>

          {/* Estado Vazio */}
          <div className="bg-white rounded-lg shadow overflow-hidden p-4">
            <h4 className="font-medium text-gray-800 mb-3">Estado Vazio</h4>
            <div className="bg-white rounded-md overflow-hidden">
              <DemandaTable
                demandas={[]}
                isLoading={false}
              />
            </div>
          </div>
        </div>

        {/* Exemplo de Código */}
        <div className="bg-white rounded-lg shadow overflow-hidden p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Exemplo de Uso</h3>
          <div className="bg-gray-900 rounded-lg p-4 text-sm overflow-x-auto">
            <pre className="text-green-400">
{`// Importação
import { DemandaTable, Pagination, Badge } from '@/components';

// Uso da Tabela com Paginação
<DemandaTable
  demandas={demandas}
  onView={handleView}
  onEdit={handleEdit}
  onDelete={handleDelete}
  isLoading={loading}
/>

<Pagination
  currentPage={currentPage}
  totalPages={totalPages}
  totalItems={totalItems}
  itemsPerPage={itemsPerPage}
  onPageChange={setCurrentPage}
  onItemsPerPageChange={setItemsPerPage}
/>

// Uso de Badges
<Badge variant="success" size="sm">Finalizada</Badge>
<Badge variant="warning" style="outline">Em Andamento</Badge>`}
            </pre>
          </div>
        </div>
      </div>

      {/* Modais */}
      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        {...(alertModal.title ? { title: alertModal.title } : {})}
        message={alertModal.message}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        type={confirmModal.type}
        {...(confirmModal.title ? { title: confirmModal.title } : {})}
        message={confirmModal.message}
        {...(confirmModal.details ? { details: confirmModal.details } : {})}
        onConfirm={confirmModal.onConfirm || (() => {})}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

export default TablesPage;